import React, { createContext, memo, useContext } from 'react';
import {
  BaseEdge,
  EdgeLabelRenderer,
  getSmoothStepPath,
  Handle,
  Position,
  useReactFlow,
} from '@xyflow/react';
import { NODE_COLORS } from './subcomponents/WorkflowNodeConfig';


/* ─── Run-state context (node status map shared with edges/nodes) ── */

/**
 * Provides per-node run statuses to custom edge and node components
 * without prop drilling through ReactFlow's internal rendering tree.
 *
 * Value shape: { nodeStatuses: Record<string, string>, isRunning: bool }
 * Status strings mirror NodeStatus from the engine:
 *   'active' (inferred), 'completed', 'failed', 'skipped'
 */
const NodeStatusCtx = createContext({ nodeStatuses: {}, isRunning: false });

/* ─── DAG ↔ xyflow conversion ──────────────────────────────── */

function dagToFlow(dag) {
  const nodes = (dag?.nodes || []).map((n) => ({
    id: n.id,
    type: `${n.config?.node_type || 'input'}Node`,
    position: n.position || { x: 0, y: 0 },
    data: { label: n.label || '', config: n.config || {} },
  }));
  const edges = (dag?.edges || []).map((e) => ({
    id: e.id,
    source: e.source,
    sourceHandle: e.source_handle || 'output',
    target: e.target,
    targetHandle: e.target_handle || 'input',
    type: 'deletable',
  }));
  return { nodes, edges };
}

function flowToDag(nodes, edges, profileName = null) {
  return {
    nodes: nodes.map((n) => ({
      id: n.id,
      label: n.data?.label || '',
      config: n.data?.config || {},
      position: n.position || { x: 0, y: 0 },
    })),
    edges: edges.map((e) => ({
      id: e.id,
      source: e.source,
      source_handle: e.sourceHandle || 'output',
      target: e.target,
      target_handle: e.targetHandle || 'input',
    })),
    // Pinned model profile for this workflow's runs (null = active profile).
    profile_name: profileName || null,
  };
}

/* ─── Custom node components ────────────────────────────────── */

const NODE_TYPE_LABELS = {
  input: 'Input',
  output: 'Output',
  agent: 'Agent',
  tool: 'Tool',
  logic: 'Logic',
};

function WorkflowNodeBase({ id, data, selected, nodeType }) {
  const color = NODE_COLORS[nodeType] || '#888';
  const label = NODE_TYPE_LABELS[nodeType] || nodeType;
  const sub = getNodeSubtitle(data?.config, nodeType);
  const { nodeStatuses } = useContext(NodeStatusCtx);
  const runSt = id ? nodeStatuses[id] : null;
  const statusClass =
    runSt === 'completed' ? ' wf-node-completed'
    : runSt === 'failed'  ? ' wf-node-failed'
    : runSt === 'running' ? ' wf-node-active'
    : runSt === 'skipped' ? ' wf-node-skipped'
    : '';

  return (
    <div className={`wf-node${selected ? ' selected' : ''}${statusClass}`}>
      <Handle
        type="target"
        position={Position.Top}
        id="input"
        style={{ background: color, width: 10, height: 10 }}
      />
      <div
        className="wf-node-header"
        style={{ background: color }}
      >
        {label}
      </div>
      <div className="wf-node-body">
        <div>{data?.label || '(unnamed)'}</div>
        {sub && <div className="wf-node-sub">{sub}</div>}
      </div>
      <Handle
        type="source"
        position={Position.Bottom}
        id="output"
        style={{ background: color, width: 10, height: 10 }}
      />
    </div>
  );
}

function getNodeSubtitle(config, nodeType) {
  if (!config) return '';
  if (nodeType === 'input' || nodeType === 'output') {
    return config.variable_name ? `var: ${config.variable_name}` : '';
  }
  if (nodeType === 'agent') {
    return config.agent_type || '';
  }
  if (nodeType === 'tool') {
    return config.tool_type || '';
  }
  if (nodeType === 'logic') {
    return config.logic_type || '';
  }
  return '';
}

const InputNode = memo((props) => (
  <WorkflowNodeBase {...props} nodeType="input" />
));
const OutputNode = memo((props) => (
  <WorkflowNodeBase {...props} nodeType="output" />
));
const AgentNode = memo((props) => (
  <WorkflowNodeBase {...props} nodeType="agent" />
));
const ToolNode = memo((props) => (
  <WorkflowNodeBase {...props} nodeType="tool" />
));
const LogicNode = memo((props) => (
  <WorkflowNodeBase {...props} nodeType="logic" />
));

const NODE_TYPES = {
  inputNode: InputNode,
  outputNode: OutputNode,
  agentNode: AgentNode,
  toolNode: ToolNode,
  logicNode: LogicNode,
};

/* ─── Custom deletable edge ──────────────────────────────────── */

/**
 * Edge with a × delete button that appears at the midpoint when the
 * edge is selected.  Clicking × calls deleteElements so the change
 * flows through onEdgesChange → pushHistory like a keyboard Delete.
 */
function DeletableEdge({
  id,
  source,
  target,
  sourceX,
  sourceY,
  sourcePosition,
  targetX,
  targetY,
  targetPosition,
  selected,
}) {
  const { deleteElements } = useReactFlow();
  const { nodeStatuses, isRunning } = useContext(NodeStatusCtx);
  const [edgePath, labelX, labelY] = getSmoothStepPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  const srcSt = nodeStatuses[source];
  const tgtSt = nodeStatuses[target];

  /* Determine run-state colour (selection always wins) */
  let stroke = '#94a3b8';
  let animated = false;
  if (selected) {
    stroke = '#3b82f6';
  } else if (srcSt === 'failed' || tgtSt === 'failed') {
    stroke = '#ef4444';
  } else if (tgtSt === 'skipped') {
    stroke = '#f59e0b';
  } else if (srcSt === 'completed' && tgtSt === 'completed') {
    stroke = '#22c55e';
  } else if (srcSt === 'completed' && isRunning) {
    /* Data is flowing: source done, target still executing */
    stroke = '#3b82f6';
    animated = true;
  }

  return (
    <>
      <BaseEdge
        id={id}
        path={edgePath}
        className={animated ? 'wf-edge-animated' : undefined}
        style={{
          stroke,
          strokeWidth: selected ? 2.5 : 2,
          transition: 'stroke 0.3s, stroke-width 0.15s',
        }}
      />
      <EdgeLabelRenderer>
        {selected && (
          <div
            className="nodrag nopan"
            style={{
              position: 'absolute',
              transform: `translate(-50%,-50%) translate(${labelX}px,${labelY}px)`,
              pointerEvents: 'all',
              zIndex: 10,
            }}
          >
            <button
              onClick={(e) => {
                e.stopPropagation();
                deleteElements({ edges: [{ id }] });
              }}
              title="Delete connection"
              style={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                background: '#ef4444',
                color: '#fff',
                border: '2px solid #fff',
                cursor: 'pointer',
                fontSize: '0.85rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                lineHeight: 1,
                boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
                padding: 0,
              }}
            >
              ×
            </button>
          </div>
        )}
      </EdgeLabelRenderer>
    </>
  );
}

const EDGE_TYPES = { deletable: DeletableEdge };

/* ─── Default configs per node type ─────────────────────────── */

function defaultConfig(nodeType) {
  switch (nodeType) {
    case 'input':
      return {
        node_type: 'input',
        variable_name: 'query',
        description: '',
        default_value: null,
      };
    case 'output':
      return {
        node_type: 'output',
        variable_name: 'result',
        description: '',
      };
    case 'agent':
      return {
        node_type: 'agent',
        agent_type: 'generalist',
        prompt_template: '{{query}}',
        output_variable: 'generalist_output',
        timeout: 230,
        web_search: false,
      };
    case 'tool':
      return {
        node_type: 'tool',
        tool_type: 'ddg_search',
        parameters: { query: '{{query}}' },
        output_variable: 'ddg_search_result',
        timeout: 60,
        soft_fail: true,
      };
    case 'logic':
      return {
        node_type: 'logic',
        logic_type: 'condition',
        condition: {
          expression: '',
          true_next: '',
          false_next: '',
        },
      };
    default:
      return { node_type: nodeType };
  }
}


export {
  NodeStatusCtx,
  dagToFlow,
  flowToDag,
  NODE_TYPE_LABELS,
  NODE_TYPES,
  EDGE_TYPES,
  defaultConfig,
};
